import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { T, th as palette, I } from "@/lib/bricks";
import { Page, PageHeader, Stats, StatusTabs, Controls, Toolbar, Search, SmallSelect, List, Row, Empty, Loading, humanize } from "@/components/calm/Calm";
import { toast } from "sonner";
import { BookMediaButton } from "@/components/listing/BookMedia";

const TIER_LABEL = { 1: 'Tier C', 2: 'Tier B', 3: 'Tier A' };

const SPECIALTY_LABELS = {
  photography: 'Photography',
  photo:       'Photography',
  drone:       'Drone',
  video:       'Video',
  virtual:     'Virtual visit',
  floor_plan:  'Floor plans',
  staging:     'Staging',
  cleaning:    'Cleaning',
};

const TYPE_LABELS = {
  photographer: 'Photographer',
  stager: 'Stager',
  cleaner: 'Cleaner',
};

function fmtRating(r) {
  if (!r) return '—';
  return r.toFixed(1);
}

export default function VendorsPage() {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [vendors, setVendors]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch]     = useState('');

  const load = () => {
    api.get('/vendors')
      .then(r => { setVendors(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleFavorite = (id) => {
    setVendors(vs => vs.map(v => v.vendor_id === id ? { ...v, is_favorite: !v.is_favorite } : v));
    api.post(`/vendors/${id}/favorite`).catch(() => {
      toast.error('Failed to update favourite');
      setVendors(vs => vs.map(v => v.vendor_id === id ? { ...v, is_favorite: !v.is_favorite } : v));
    });
  };

  const filtered = vendors.filter(v => {
    const matchesSearch = !search ||
      v.name.toLowerCase().includes(search.toLowerCase()) ||
      (v.specialty || '').toLowerCase().includes(search.toLowerCase());
    const matchesTab =
      activeTab === 'All'        ? true :
      activeTab === 'Favourites' ? v.is_favorite :
      (v.vendor_type || 'photographer').toLowerCase() === activeTab.toLowerCase();
    return matchesSearch && matchesTab;
  });

  const totalFavourites = vendors.filter(v => v.is_favorite).length;
  const rated = vendors.filter(v => v.rating);
  const avgRating = rated.length
    ? (rated.reduce((s, v) => s + v.rating, 0) / rated.length).toFixed(1)
    : 'None yet';

  if (loading) return <Loading />;

  const typeOf = (v) => (v.vendor_type || 'photographer').toLowerCase();
  const types = ['photographer', 'stager', 'cleaner'].filter(t => vendors.some(v => typeOf(v) === t));

  return (
    <Page testid="vendors-page">
      <PageHeader title="Photographers" subtitle="Favourites get priority dispatch for your agents' bookings"
        action={<BookMediaButton dark={dark} testId="vendors-book" />} />

      <Stats items={[
        { label: 'Vendors', value: vendors.length },
        { label: 'Favourites', value: totalFavourites },
        { label: 'Average rating', value: avgRating },
      ]} />

      <Controls>
        <StatusTabs testid="vendors-filter" value={activeTab === 'Favourites' ? 'Favourites' : 'All'} onChange={(k) => setActiveTab(k)}
          options={[{ key: 'All', label: 'All' }, { key: 'Favourites', label: 'Favourites' }]} />
        <Toolbar>
          <Search testid="vendors-search" value={search} onChange={setSearch} placeholder="Search vendors" />
          {types.length > 1 && (
            <SmallSelect testid="vendors-type" label="Type"
              value={['Photographer', 'Stager', 'Cleaner'].includes(activeTab) ? activeTab : 'All'}
              onChange={(k) => setActiveTab(k)}
              options={[{ key: 'All', label: 'Any type' }, ...types.map(t => ({ key: TYPE_LABELS[t], label: TYPE_LABELS[t] }))]} />
          )}
        </Toolbar>
      </Controls>

      {filtered.length === 0 ? (
        <Empty>{search ? 'No vendors match that search.' : activeTab === 'Favourites' ? 'No favourites yet. Tap the heart on a vendor to give them priority.' : 'No vendors here yet.'}</Empty>
      ) : (
        <List testid="vendors-list">
          {filtered.map(vendor => {
            const tier = vendor.tier || 1;
            const role = vendor.role === 'Owner' ? 'Owner' : (TYPE_LABELS[vendor.vendor_type] || SPECIALTY_LABELS[vendor.specialty] || humanize(vendor.specialty) || 'Photographer');
            return (
              <Row
                key={vendor.vendor_id}
                testid="vendor-row"
                title={vendor.name}
                meta={[role, TIER_LABEL[tier] || 'Tier C', vendor.role === 'Owner' ? 'Full package' : 'Rate card']}
                next={[
                  vendor.rating ? `${fmtRating(vendor.rating)} rating` : 'No rating yet',
                  vendor.total_jobs > 0 ? `${vendor.total_jobs} jobs` : null,
                  vendor.is_available === undefined ? null : (vendor.is_available ? 'Available' : 'Unavailable'),
                ].filter(Boolean)}
                aside={
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(vendor.vendor_id); }}
                    aria-pressed={!!vendor.is_favorite}
                    aria-label={vendor.is_favorite ? 'Remove favourite' : 'Add to favourites'}
                    title={vendor.is_favorite ? 'Remove favourite' : 'Add to favourites'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', width: 44, height: 44, margin: -10, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0 }}
                  >
                    <I name="heart" size={17} color={vendor.is_favorite ? T.brickDeep : p.ink4} />
                  </button>
                }
              />
            );
          })}
        </List>
      )}
    </Page>
  );
}
